import { useEffect, useState, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Siren, Wrench, ShieldAlert, PackageX, AlertOctagon, CheckCircle2,
  Clock, Loader2, Radio, RefreshCw,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

type SOS = {
  id: number; maquina_id: number; maquina_codigo: string; maquina_nome: string
  categoria: string; observacao: string | null; status: string
  op_id: number | null; op_numero: number | null
  aberto_em: string; atendido_em: string | null
  resolvido_em: string | null; resolucao: string | null
  minutos_aberto: number
  aberto_por_id: number | null; atendido_por_id: number | null
}

const CAT_META: Record<string, { icon: any; cor: string; label: string }> = {
  manutencao: { icon: Wrench, cor: 'bg-red-100 text-red-800 border-red-300', label: 'Manutenção' },
  qualidade: { icon: ShieldAlert, cor: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Qualidade' },
  falta_mp: { icon: PackageX, cor: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Falta MP' },
  seguranca: { icon: Siren, cor: 'bg-rose-100 text-rose-800 border-rose-300', label: 'Segurança' },
  outro: { icon: AlertOctagon, cor: 'bg-ink-100 text-ink-800 border-ink-300', label: 'Outro' },
}

export default function SOSPanelPage() {
  const qc = useQueryClient()
  const [wsConnected, setWsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)

  const { data: sos = [], isLoading, refetch } = useQuery<SOS[]>({
    queryKey: ['sos-abertos'],
    queryFn: async () => (await api.get('/industrial/sos')).data,
    refetchInterval: 30000,  // polling fallback
  })

  // WebSocket pra atualização em tempo real
  useEffect(() => {
    const token = localStorage.getItem('pain_token')
    if (!token) return
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = `${proto}//${window.location.host}/api/industrial/andon/stream?token=${encodeURIComponent(token)}`
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => setWsConnected(true)
    ws.onclose = () => setWsConnected(false)
    ws.onerror = () => setWsConnected(false)
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data)
        if (['andon.sos', 'andon.sos_atendido', 'andon.sos_resolvido'].includes(msg.evento)) {
          qc.invalidateQueries({ queryKey: ['sos-abertos'] })
          if (msg.evento === 'andon.sos') {
            toast(`🚨 SOS ${msg.payload.categoria} — máquina ${msg.payload.maquina_codigo}`, {
              duration: 8000, icon: '🚨',
            })
          }
        }
      } catch {}
    }

    return () => { ws.close() }
  }, [qc])

  async function atender(id: number) {
    try {
      await api.post(`/industrial/sos/${id}/atender`)
      toast.success('Atendendo')
      refetch()
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro') }
  }

  async function resolver(id: number) {
    const resolucao = prompt('Como foi resolvido? (opcional)')
    if (resolucao === null) return  // cancelou
    try {
      await api.post(`/industrial/sos/${id}/resolver`, { resolucao: resolucao || undefined })
      toast.success('SOS resolvido')
      refetch()
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro') }
  }

  const abertos = sos.filter(s => s.status === 'aberto')
  const atendidos = sos.filter(s => s.status === 'atendido')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Siren className="h-6 w-6 text-rose-600" /> Andon · SOS
        </h1>
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${wsConnected ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
            <Radio className={`h-3 w-3 ${wsConnected ? 'animate-pulse' : ''}`} />
            {wsConnected ? 'Tempo real conectado' : 'Reconectando...'}
          </span>
          <button onClick={() => refetch()} className="h-9 w-9 rounded-lg bg-ink-100 hover:bg-ink-200 flex items-center justify-center">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ABERTOS */}
      <section>
        <h2 className="text-sm font-semibold uppercase text-rose-700 mb-2">
          🚨 Abertos ({abertos.length})
        </h2>
        {isLoading ? (
          <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-rose-600 mx-auto" /></div>
        ) : abertos.length === 0 ? (
          <div className="bg-white rounded-lg border border-ink-200 p-8 text-center text-ink-500">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-2" />
            Nenhum SOS aberto. Tudo tranquilo no chão.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {abertos.map(s => <SOSCard key={s.id} sos={s} onAtender={atender} onResolver={resolver} novo />)}
          </div>
        )}
      </section>

      {/* ATENDIDOS */}
      {atendidos.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold uppercase text-amber-700 mb-2">
            ⏳ Em atendimento ({atendidos.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {atendidos.map(s => <SOSCard key={s.id} sos={s} onAtender={atender} onResolver={resolver} />)}
          </div>
        </section>
      )}
    </div>
  )
}


function SOSCard({ sos, onAtender, onResolver, novo }: {
  sos: SOS; onAtender: (id: number) => void; onResolver: (id: number) => void; novo?: boolean
}) {
  const meta = CAT_META[sos.categoria] || CAT_META.outro
  const Icon = meta.icon

  return (
    <div className={`bg-white rounded-xl border-2 ${meta.cor.split(' ').filter(c => c.startsWith('border')).join(' ')} p-4 space-y-3 ${novo ? 'animate-pulse-slow ring-2 ring-rose-300' : ''}`}>
      <div className="flex items-start justify-between">
        <div className={`px-2 py-1 rounded text-xs font-bold uppercase flex items-center gap-1 ${meta.cor}`}>
          <Icon className="h-3 w-3" /> {meta.label}
        </div>
        <div className="text-xs text-ink-500 flex items-center gap-1">
          <Clock className="h-3 w-3" /> {sos.minutos_aberto}min
        </div>
      </div>

      <div>
        <div className="font-bold text-ink-900">{sos.maquina_codigo}</div>
        <div className="text-xs text-ink-500">{sos.maquina_nome}</div>
        {sos.op_numero && <div className="text-xs text-violet-700 mt-1">OP #{sos.op_numero}</div>}
      </div>

      {sos.observacao && (
        <div className="text-sm text-ink-700 bg-ink-50 rounded p-2 italic">"{sos.observacao}"</div>
      )}

      <div className="flex gap-2 pt-2 border-t border-ink-100">
        {sos.status === 'aberto' && (
          <button
            onClick={() => onAtender(sos.id)}
            className="flex-1 h-10 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg"
          >
            Atender
          </button>
        )}
        <button
          onClick={() => onResolver(sos.id)}
          className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg"
        >
          Resolver
        </button>
      </div>
    </div>
  )
}
